import type { ReactNode } from "react";
import type { Contact, ReportContact, RelationshipStatus } from "@/types/report-types";

/**
 * Clean, primitive-facing data shape for ContactEntity. Upstream shapes
 * (`Contact`, `ReportContact`) are adapted via `toContactEntityData`.
 */
export interface ContactEntityData {
  id: string;
  fullName: string;
  title?: string;
  company?: { id?: string; name: string };
  location?: string;
  email?: string;
  emails?: string[];
  phones?: string[];
  linkedInUrl?: string;
  seniority?: string;
  tags?: string[];
  relevanceScore?: number;
  relevanceReason?: string;
  status?: RelationshipStatus;
  isFavorite?: boolean;
  hasOutreach?: boolean;
  spotlight?: boolean;
  spotlightScore?: number;
  relevanceLikelihood?: number;
  responseLikelihood?: number;
  hiringImpactLikelihood?: number;
  /**
   * The primary matched open role this contact was surfaced for. Set by the
   * simple-search pipeline so pool-backed outreach cards (rebuilt as company-only
   * groups without a `job`) can still show the role that anchored the card. The
   * live deck carries the real job on the card instead, so this is a fallback.
   */
  matchedJobTitle?: string;
  /**
   * Original Contact passthrough so handlers that were typed on the raw
   * Contact shape (quick view, reach out, etc.) still receive the full
   * upstream object. The primitive never inspects it.
   */
  sourceContact?: Contact | ReportContact;
}

export interface ContactEntityFields {
  email?: boolean;
  phone?: boolean;
  linkedIn?: boolean;
  location?: boolean;
  tags?: boolean;
  relevance?: boolean;
  seniority?: boolean;
  status?: boolean;
}

export interface ContactEntityActions {
  selection?: boolean;
  statusPicker?: boolean;
  /** Show a "Save" button that persists the contact as a saved opportunity. */
  saveOpportunity?: boolean;
  favorite?: boolean;
  library?: boolean;
  quickView?: boolean;
  viewProfile?: boolean;
  reachOut?: boolean;
  viewReport?: boolean;
}

export interface ContactLibraryState {
  isInLibrary: boolean;
  isAdding?: boolean;
}

export type ContactEntityLayout = "card" | "row";

export interface ContactEntityProps {
  contact: ContactEntityData;
  layout?: ContactEntityLayout;
  density?: "comfortable" | "compact";

  /** Which metadata rows / badges to render. Defaults depend on layout. */
  fields?: ContactEntityFields;
  /** Which action buttons to render. */
  actions?: ContactEntityActions;

  /* Handlers — all optional; button is hidden if both the corresponding
   * `actions.X` flag is falsy AND the handler is missing. */
  onSelect?: (contact: ContactEntityData) => void;
  onStatusChange?: (status: RelationshipStatus) => void;
  /** Save this contact + company as a saved opportunity. */
  onSaveOpportunity?: (contact: ContactEntityData) => void;
  onToggleFavorite?: (contact: ContactEntityData) => void;
  onAddToLibrary?: (contact: ContactEntityData) => void;
  onQuickView?: (contact: ContactEntityData) => void;
  onViewProfile?: (contact: ContactEntityData) => void;
  onReachOut?: (
    contact: ContactEntityData,
    preferredRecipientEmail?: string,
  ) => void;
  onOpenReport?: (contact: ContactEntityData) => void;

  /* State */
  isSelected?: boolean;
  isFavorite?: boolean;
  libraryState?: ContactLibraryState;
  /** Tracks save-opportunity progress: isSaved disables the button, isSaving shows spinner. */
  saveOpportunityState?: { isSaved: boolean; isSaving: boolean };
  reachOutDisabled?: boolean;
  paywallMode?: boolean;

  /* Status picker */
  statusPickerDefaultOpen?: boolean;

  /* Additional presentation */
  lookupRoleLabel?: string;
  groupLabel?: string;
  reportId?: string | null;
  /** Show reveal-style hints for hidden contact fields without full paywall action mode. */
  showHiddenFieldHints?: boolean;
  badge?: ReactNode;
  /** Rendered at the very start of the chips row (e.g. OutreachPriorityBadge). */
  priorityBadge?: ReactNode;
  /** Rendered at the bottom of the content column, above the action row. Used for "Why Contact". */
  footer?: ReactNode;
  trackingContext?: string;
  className?: string;
}

/* ----------------------------- Adapters ---------------------------------- */

/**
 * Adapt a `Contact` (dashboard type) or `ReportContact` (report type)
 * into a `ContactEntityData` view-model. Additional fields from the
 * source are stashed on `sourceContact` so legacy handlers keep
 * receiving the raw object.
 */
export function toContactEntityData(
  contact: Contact | ReportContact,
): ContactEntityData {
  const anyContact = contact as Contact & ReportContact & {
    location?: string;
  };
  // `company` is typed as a string but SOME upstream contacts carry it as an
  // object ({ id, name }). Concatenating that object rendered "[object Object]"
  // on the contacts list. Normalize both shapes to a plain name + optional id.
  const rawCompany = anyContact.company as unknown;
  const companyName =
    typeof rawCompany === "string"
      ? rawCompany
      : rawCompany && typeof rawCompany === "object"
        ? String((rawCompany as { name?: unknown }).name ?? "")
        : "";
  const companyId =
    anyContact.companyId ??
    (rawCompany && typeof rawCompany === "object"
      ? (rawCompany as { id?: unknown }).id
      : undefined);
  return {
    id: anyContact.id,
    fullName: anyContact.fullName,
    title: anyContact.title || undefined,
    company:
      companyName || companyId != null
        ? {
            id: companyId != null ? String(companyId) : undefined,
            name: companyName,
          }
        : undefined,
    location: anyContact.location || undefined,
    email: anyContact.email || undefined,
    emails: anyContact.emails,
    phones: anyContact.phones,
    linkedInUrl: anyContact.linkedIn || undefined,
    seniority: anyContact.seniority || undefined,
    tags: anyContact.tags ?? [],
    relevanceScore:
      typeof anyContact.relevanceScore === "number"
        ? anyContact.relevanceScore
        : undefined,
    relevanceReason: anyContact.relevanceReason || undefined,
    status: anyContact.status as RelationshipStatus | undefined,
    isFavorite: anyContact.isFavorite,
    hasOutreach: anyContact.hasOutreach,
    spotlight: anyContact.spotlight,
    spotlightScore: anyContact.spotlightScore,
    relevanceLikelihood: anyContact.relevanceLikelihood,
    responseLikelihood: anyContact.responseLikelihood,
    hiringImpactLikelihood: anyContact.hiringImpactLikelihood,
    sourceContact: contact,
  };
}
